import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { fetchProfile, saveProfile, requestResumeUploadUrl, deleteResume, processResume } from '../lib/profile-api';
import { deleteAccount } from '../lib/account-api';
import { getUserIdentity, type UserIdentity } from '../lib/auth';
import type { CareerEntry, EducationEntry, FamilyMember, UserProfile } from '../types/profile';

const WIZARD_STEPS = ['basic', 'education', 'career', 'family', 'travel', 'hobbies'] as const;
type WizardStep = (typeof WIZARD_STEPS)[number];

// 3-step first-fill wizard (Phase 54 — simplified onboarding)
const WIZARD_3_STEPS = ['basic', 'hometown', 'family'] as const;
type Wizard3Step = (typeof WIZARD_3_STEPS)[number];

const WIZARD_3_HEADINGS: Record<Wizard3Step, { heading: string; subheading?: string }> = {
  basic: { heading: 'Tell us about yourself', subheading: 'Start with the basics — you can always edit later.' },
  hometown: { heading: 'Where did you grow up?' },
  family: { heading: "Who's in your family?" },
};

// Map wizard 3-step name to the section to render (reuse existing renderSection)
const WIZARD_3_SECTION: Record<Wizard3Step, WizardStep> = {
  basic: 'basic',
  hometown: 'basic', // renders the basic section; hometown field is inside renderBasic
  family: 'family',
};

type ProfileTab = WizardStep | 'settings';

const STEP_LABELS: Record<WizardStep, string> = {
  basic: 'Basic Info',
  education: 'Education',
  career: 'Career',
  family: 'Family',
  travel: 'Travel',
  hobbies: 'Hobbies',
};

const TAB_LABELS: ProfileTab[] = ['basic', 'education', 'career', 'family', 'travel', 'hobbies', 'settings'];

const INPUT_CLS =
  'w-full px-4 py-3 rounded-xl border border-stone-300 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-colors';
const TEXTAREA_CLS = INPUT_CLS + ' resize-none';
const LABEL_CLS = 'block text-sm font-medium text-stone-700 mb-1';
const HELP_CLS = 'text-xs text-stone-400 mt-1';
const PRIMARY_BTN =
  'px-6 py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2';
const SECONDARY_BTN =
  'px-6 py-3 rounded-xl border border-stone-300 text-stone-700 font-semibold hover:bg-stone-100 transition-colors focus:outline-none focus:ring-2 focus:ring-stone-400 focus:ring-offset-2';
const ENTRY_CARD_CLS = 'bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-3';

export function Profile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile>({});
  const [isFirstFill, setIsFirstFill] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [activeTab, setActiveTab] = useState<ProfileTab>('basic');
  const [savedSection, setSavedSection] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [accountDeleted, setAccountDeleted] = useState(false);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeProcessing, setResumeProcessing] = useState(false);
  const [resumeProcessed, setResumeProcessed] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [identity, setIdentity] = useState<UserIdentity | null>(null);

  // Debounce timer ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mounted guard — prevents state updates on an unmounted component (WR-04)
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    fetchProfile()
      .then((fetched) => {
        if (fetched === null) {
          setIsFirstFill(true);
          setProfile({});
        } else {
          setIsFirstFill(false);
          setProfile(fetched);
        }
        setLoading(false);
      })
      .catch(() => {
        // On error, treat as first fill
        setIsFirstFill(true);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    getUserIdentity().then(setIdentity).catch(() => undefined);
  }, []);

  // Auto-save on blur — debounced 300ms
  const handleBlurSave = useCallback(
    (section: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        saveProfile(profile)
          .then(() => {
            setSavedSection(section);
            setSaveError(null);
            setTimeout(() => setSavedSection(null), 3000);
          })
          .catch(() => {
            setSaveError("Couldn't save changes. Please try again.");
          });
      }, 300);
    },
    [profile],
  );

  const updateField = <K extends keyof UserProfile>(key: K, value: UserProfile[K]) => {
    setProfile((prev) => ({ ...prev, [key]: value }));
  };

  // ── Wizard navigation ──────────────────────────────────────────────────────

  const canAdvance = () => {
    if (currentStep === 0) {
      return (profile.fullName ?? '').trim().length > 0;
    }
    return true;
  };

  const wizardLength = isFirstFill ? WIZARD_3_STEPS.length : WIZARD_STEPS.length;

  const handleNext = () => {
    if (!canAdvance()) return;
    handleBlurSave(isFirstFill ? WIZARD_3_STEPS[currentStep] : WIZARD_STEPS[currentStep]);
    setCurrentStep((s) => Math.min(s + 1, wizardLength - 1));
  };

  const handleSkip = () => {
    setCurrentStep((s) => Math.min(s + 1, wizardLength - 1));
  };

  const handleBack = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const handleStartOver = () => {
    if (!window.confirm('Clear this profile form and start over? Your stories, sessions, photos, and book will not be changed.')) return;
    setProfile({});
    setCurrentStep(0);
    setSaveError(null);
  };

  const handleFinish = () => {
    saveProfile(profile)
      .then(() => navigate(isFirstFill ? '/stories' : '/'))
      .catch(() => {
        setSaveError("Couldn't save changes. Please try again.");
      });
  };

  async function handleResumeUpload(file: File) {
    const allowed = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowed.includes(file.type)) {
      setResumeError('Please upload a PDF or Word document (.pdf, .doc, .docx)');
      return;
    }
    setResumeUploading(true);
    setResumeError(null);
    setResumeProcessed(false);
    try {
      const { uploadUrl, key } = await requestResumeUploadUrl(file.name, file.type);
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putRes.ok) throw new Error('Upload failed');
      const updated = { ...profile, resumeKey: key, resumeFileName: file.name };
      setProfile(updated);
      await saveProfile(updated);
      setSavedSection('career');
      setTimeout(() => setSavedSection(null), 3000);
      // Fire-and-forget: process resume in background
      setResumeProcessing(true);
      processResume()
        .then((result) => {
          if (mountedRef.current) {
            setProfile((prev) => ({ ...prev, resumeCareerSummary: result.resumeCareerSummary }));
            setResumeProcessing(false);
            setResumeProcessed(true);
          }
        })
        .catch(() => {
          if (mountedRef.current) {
            setResumeProcessing(false);
          }
        });
    } catch {
      setResumeError('Upload failed. Please try again.');
    } finally {
      setResumeUploading(false);
    }
  }

  // ── Section renderers ──────────────────────────────────────────────────────

  const currentSection: ProfileTab = isFirstFill
    ? WIZARD_3_SECTION[WIZARD_3_STEPS[currentStep]]
    : activeTab;

  function renderBasic() {
    return (
      <div className="space-y-5">
        {isFirstFill && (
          <p className="text-stone-600 text-sm">
            Tell us about yourself so your memoir feels personal from the very first question.
          </p>
        )}
        <div>
          <label className={LABEL_CLS} htmlFor="fullName">Full name</label>
          <input
            id="fullName"
            type="text"
            className={INPUT_CLS}
            value={profile.fullName ?? ''}
            onChange={(e) => updateField('fullName', e.target.value)}
            onBlur={() => handleBlurSave('basic')}
          />
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor="birthday">Birth year</label>
          <input
            id="birthday"
            type="text"
            className={INPUT_CLS}
            placeholder="e.g. 1945"
            value={profile.birthday ?? ''}
            onChange={(e) => updateField('birthday', e.target.value)}
            onBlur={() => handleBlurSave('basic')}
          />
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor="hometown">Where you grew up</label>
          <input
            id="hometown"
            type="text"
            className={INPUT_CLS}
            placeholder="e.g. Detroit, Michigan"
            value={profile.hometown ?? ''}
            onChange={(e) => updateField('hometown', e.target.value)}
            onBlur={() => handleBlurSave('basic')}
          />
        </div>
      </div>
    );
  }

  function renderEducation() {
    const entries = profile.educationEntries ?? [];
    return (
      <div className="space-y-5">
        <div>
          <label className={LABEL_CLS} htmlFor="educationText">Education</label>
          <textarea
            id="educationText"
            rows={4}
            className={TEXTAREA_CLS}
            placeholder="University of Michigan, Engineering, 1972 — or use the form below to add structured entries"
            value={profile.educationText ?? ''}
            onChange={(e) => updateField('educationText', e.target.value)}
            onBlur={() => handleBlurSave('education')}
          />
        </div>
        <button
          type="button"
          className="text-sm text-amber-600 hover:underline cursor-pointer"
          onClick={() => {
            const newEntry: EducationEntry = { institution: '' };
            updateField('educationEntries', [...entries, newEntry]);
          }}
        >
          Add structured entry
        </button>
        {entries.map((entry, i) => (
          <div key={i} className={ENTRY_CARD_CLS}>
            <div className="flex justify-between items-start">
              <span className="text-xs font-medium text-stone-500">Entry {i + 1}</span>
              <button
                type="button"
                aria-label="Remove education entry"
                className="text-stone-400 hover:text-red-600"
                onClick={() => {
                  const updated = entries.filter((_, idx) => idx !== i);
                  updateField('educationEntries', updated);
                }}
              >
                ×
              </button>
            </div>
            <div>
              <label className={LABEL_CLS}>Institution</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={entry.institution}
                onChange={(e) => {
                  const updated = entries.map((en, idx) =>
                    idx === i ? { ...en, institution: e.target.value } : en,
                  );
                  updateField('educationEntries', updated);
                }}
                onBlur={() => handleBlurSave('education')}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL_CLS}>Degree</label>
                <input
                  type="text"
                  className={INPUT_CLS}
                  value={entry.degree ?? ''}
                  onChange={(e) => {
                    const updated = entries.map((en, idx) =>
                      idx === i ? { ...en, degree: e.target.value } : en,
                    );
                    updateField('educationEntries', updated);
                  }}
                  onBlur={() => handleBlurSave('education')}
                />
              </div>
              <div>
                <label className={LABEL_CLS}>Year</label>
                <input
                  type="text"
                  className={INPUT_CLS}
                  value={entry.year ?? ''}
                  onChange={(e) => {
                    const updated = entries.map((en, idx) =>
                      idx === i ? { ...en, year: e.target.value } : en,
                    );
                    updateField('educationEntries', updated);
                  }}
                  onBlur={() => handleBlurSave('education')}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  function renderCareer() {
    const entries = profile.careerEntries ?? [];
    return (
      <div className="space-y-5">
        <div>
          <label className={LABEL_CLS} htmlFor="careerText">Career</label>
          <textarea
            id="careerText"
            rows={4}
            className={TEXTAREA_CLS}
            placeholder="35 years in manufacturing, plant manager at Acme Corp 1985–2002 — or add structured entries below"
            value={profile.careerText ?? ''}
            onChange={(e) => updateField('careerText', e.target.value)}
            onBlur={() => handleBlurSave('career')}
          />
        </div>
        <button
          type="button"
          className="text-sm text-amber-600 hover:underline cursor-pointer"
          onClick={() => {
            const newEntry: CareerEntry = { company: '', role: '' };
            updateField('careerEntries', [...entries, newEntry]);
          }}
        >
          Add structured entry
        </button>
        {entries.map((entry, i) => (
          <div key={i} className={ENTRY_CARD_CLS}>
            <div className="flex justify-between items-start">
              <span className="text-xs font-medium text-stone-500">Entry {i + 1}</span>
              <button
                type="button"
                aria-label="Remove career entry"
                className="text-stone-400 hover:text-red-600"
                onClick={() => {
                  const updated = entries.filter((_, idx) => idx !== i);
                  updateField('careerEntries', updated);
                }}
              >
                ×
              </button>
            </div>
            <div>
              <label className={LABEL_CLS}>Company</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={entry.company}
                onChange={(e) => {
                  const updated = entries.map((en, idx) =>
                    idx === i ? { ...en, company: e.target.value } : en,
                  );
                  updateField('careerEntries', updated);
                }}
                onBlur={() => handleBlurSave('career')}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL_CLS}>Role</label>
                <input
                  type="text"
                  className={INPUT_CLS}
                  value={entry.role}
                  onChange={(e) => {
                    const updated = entries.map((en, idx) =>
                      idx === i ? { ...en, role: e.target.value } : en,
                    );
                    updateField('careerEntries', updated);
                  }}
                  onBlur={() => handleBlurSave('career')}
                />
              </div>
              <div>
                <label className={LABEL_CLS}>Years</label>
                <input
                  type="text"
                  className={INPUT_CLS}
                  value={entry.years ?? ''}
                  onChange={(e) => {
                    const updated = entries.map((en, idx) =>
                      idx === i ? { ...en, years: e.target.value } : en,
                    );
                    updateField('careerEntries', updated);
                  }}
                  onBlur={() => handleBlurSave('career')}
                />
              </div>
            </div>
          </div>
        ))}
        {/* Resume upload */}
        <div className="border-t border-stone-200 pt-4 mt-4">
          <label className={LABEL_CLS}>Resume (optional)</label>
          <p className={HELP_CLS}>Upload your resume to help Claude ask more specific questions about your career.</p>
          {profile.resumeFileName ? (
            <div className="flex items-center gap-3 mt-2">
              <span className="text-sm text-stone-700 bg-stone-100 rounded-lg px-3 py-2 flex-1 truncate">
                {profile.resumeFileName}
              </span>
              <button
                type="button"
                className="text-sm text-red-500 hover:text-red-700"
                onClick={() => {
                  const updated = { ...profile, resumeKey: undefined, resumeFileName: undefined };
                  setProfile(updated);
                  deleteResume().catch(() => {});
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            <label className="mt-2 flex items-center gap-3 cursor-pointer">
              <span className={`${PRIMARY_BTN} text-sm py-2 px-4 ${resumeUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {resumeUploading ? 'Uploading…' : 'Choose file'}
              </span>
              <span className="text-sm text-stone-400">PDF, DOC, or DOCX</span>
              <input
                type="file"
                className="sr-only"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                disabled={resumeUploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleResumeUpload(file).catch(() => {});
                  e.target.value = '';
                }}
              />
            </label>
          )}
          {resumeProcessing && (
            <p className="text-sm text-stone-500 mt-2">Analyzing resume...</p>
          )}
          {resumeProcessed && !resumeProcessing && (
            <p className="text-sm text-green-600 mt-2">Resume analyzed</p>
          )}
          {resumeError && <p className="text-sm text-red-600 mt-1">{resumeError}</p>}
        </div>
      </div>
    );
  }

  function renderFamily() {
    const members = profile.familyMembers ?? [];
    return (
      <div className="space-y-5">
        <p className={HELP_CLS + ' text-stone-500 text-sm'}>
          Adding family members helps Claude ask &ldquo;What was it like when Jake was born?&rdquo; from day one.
        </p>
        {members.map((member, i) => (
          <div key={i} className={ENTRY_CARD_CLS}>
            <div className="flex justify-between items-start">
              <span className="text-xs font-medium text-stone-500">Member {i + 1}</span>
              <button
                type="button"
                aria-label="Remove family member"
                className="text-stone-400 hover:text-red-600"
                onClick={() => {
                  const updated = members.filter((_, idx) => idx !== i);
                  updateField('familyMembers', updated);
                }}
              >
                ×
              </button>
            </div>
            <div>
              <label className={LABEL_CLS}>Name</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={member.name}
                onChange={(e) => {
                  const updated = members.map((m, idx) =>
                    idx === i ? { ...m, name: e.target.value } : m,
                  );
                  updateField('familyMembers', updated);
                }}
                onBlur={() => handleBlurSave('family')}
              />
            </div>
            <div>
              <label className={LABEL_CLS}>Relationship</label>
              <select
                className={INPUT_CLS}
                value={member.relationship}
                onChange={(e) => {
                  const updated = members.map((m, idx) =>
                    idx === i
                      ? { ...m, relationship: e.target.value as FamilyMember['relationship'] }
                      : m,
                  );
                  updateField('familyMembers', updated);
                }}
                onBlur={() => handleBlurSave('family')}
              >
                <option value="Spouse">Spouse</option>
                <option value="Child">Child</option>
                <option value="Sibling">Sibling</option>
                <option value="Parent">Parent</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className={LABEL_CLS}>Notes (optional)</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={member.notes ?? ''}
                onChange={(e) => {
                  const updated = members.map((m, idx) =>
                    idx === i ? { ...m, notes: e.target.value } : m,
                  );
                  updateField('familyMembers', updated);
                }}
                onBlur={() => handleBlurSave('family')}
              />
            </div>
          </div>
        ))}
        <button
          type="button"
          className="text-sm text-stone-600 border border-stone-300 rounded-lg px-3 py-2 hover:bg-stone-100 transition-colors"
          onClick={() => {
            const newMember: FamilyMember = { name: '', relationship: 'Spouse' };
            updateField('familyMembers', [...members, newMember]);
          }}
        >
          Add family member
        </button>
      </div>
    );
  }

  function renderTravel() {
    return (
      <div className="space-y-5">
        <div>
          <label className={LABEL_CLS} htmlFor="travel">Travel</label>
          <textarea
            id="travel"
            rows={5}
            className={TEXTAREA_CLS}
            placeholder="Japan 1995, backpacked South America in my 20s, annual fishing trips to Montana"
            value={profile.travel ?? ''}
            onChange={(e) => updateField('travel', e.target.value)}
            onBlur={() => handleBlurSave('travel')}
          />
          <p className={HELP_CLS}>Places you&rsquo;ve lived, visited, or that shaped you.</p>
        </div>
      </div>
    );
  }

  function renderHobbies() {
    return (
      <div className="space-y-5">
        <div>
          <label className={LABEL_CLS} htmlFor="hobbies">Hobbies &amp; interests</label>
          <textarea
            id="hobbies"
            rows={5}
            className={TEXTAREA_CLS}
            placeholder="Woodworking since the 70s, avid reader (history and biography), Sunday golf"
            value={profile.hobbies ?? ''}
            onChange={(e) => updateField('hobbies', e.target.value)}
            onBlur={() => handleBlurSave('hobbies')}
          />
          <p className={HELP_CLS}>Activities, passions, and pastimes — past and present.</p>
        </div>
      </div>
    );
  }

  function renderSettings() {
    const style = profile.interviewStyle ?? 'story';
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-base font-semibold text-stone-900 mb-1">Interview Style</h3>
          <p className="text-sm text-stone-500 mb-4">
            Choose how your interviewer approaches each session.
          </p>
          <div className="space-y-3">
            <label className="flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-colors"
              style={{ borderColor: style === 'story' ? '#f59e0b' : '#e7e5e4', background: style === 'story' ? '#fffbeb' : '#fff' }}
            >
              <input
                type="radio"
                name="interviewStyle"
                value="story"
                checked={style === 'story'}
                onChange={() => {
                  updateField('interviewStyle', 'story');
                  saveProfile({ ...profile, interviewStyle: 'story' }).catch(() => {});
                }}
                className="mt-1 accent-amber-500"
              />
              <div>
                <div className="font-semibold text-stone-900 text-sm">Story Mode</div>
                <div className="text-sm text-stone-500 mt-0.5">
                  The interviewer asks for a specific memory or story first, then digs into the details and feelings. Best for capturing rich, vivid narratives.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-colors"
              style={{ borderColor: style === 'reflective' ? '#f59e0b' : '#e7e5e4', background: style === 'reflective' ? '#fffbeb' : '#fff' }}
            >
              <input
                type="radio"
                name="interviewStyle"
                value="reflective"
                checked={style === 'reflective'}
                onChange={() => {
                  updateField('interviewStyle', 'reflective');
                  saveProfile({ ...profile, interviewStyle: 'reflective' }).catch(() => {});
                }}
                className="mt-1 accent-amber-500"
              />
              <div>
                <div className="font-semibold text-stone-900 text-sm">Reflective Mode</div>
                <div className="text-sm text-stone-500 mt-0.5">
                  The interviewer guides you through topics and time periods, asking how you felt about different chapters of your life.
                </div>
              </div>
            </label>
          </div>
        </div>
        <div className="mt-8">
          <div className="border border-red-200 bg-red-50 rounded-xl p-6 space-y-4">
            <h3 className="text-base font-semibold text-red-700">Danger Zone</h3>
            <p className="text-sm text-stone-700">Deleting your account will permanently remove:</p>
            <ul className="text-sm text-stone-700 list-disc list-inside space-y-1">
              <li>All interview sessions</li>
              <li>All photos</li>
              <li>Your memoir book</li>
              <li>Your account</li>
            </ul>
            <label htmlFor="delete-confirm" className="text-sm text-stone-700 block">
              Type <strong>DELETE</strong> to confirm:
            </label>
            <input
              id="delete-confirm"
              type="text"
              className={INPUT_CLS}
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="DELETE"
              disabled={isDeleting}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              aria-disabled={isDeleting}
            />
            {deleteError && (
              <p className="text-sm text-red-600">{deleteError}</p>
            )}
            <button
              type="button"
              className="px-6 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
              disabled={confirmText !== 'DELETE' || isDeleting}
              aria-disabled={confirmText !== 'DELETE' || isDeleting}
              onClick={async () => {
                setIsDeleting(true);
                setDeleteError(null);
                try {
                  await deleteAccount();
                  if (mountedRef.current) { setAccountDeleted(true); }
                  await signOut(); // clears Amplify token cache immediately (CR-03)
                  setTimeout(() => { window.location.href = 'https://mylifehistory.xyz'; }, 3000);
                } catch {
                  if (mountedRef.current) {
                    setDeleteError('Something went wrong. Please try again.');
                    setIsDeleting(false);
                  }
                }
              }}
            >
              {isDeleting ? 'Deleting your account…' : 'Delete Account'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderSection(section: ProfileTab) {
    switch (section) {
      case 'basic': return renderBasic();
      case 'education': return renderEducation();
      case 'career': return renderCareer();
      case 'family': return renderFamily();
      case 'travel': return renderTravel();
      case 'hobbies': return renderHobbies();
      case 'settings': return renderSettings();
    }
  }

  // ── Loading state ──────────────────────────────────────────────────────────

  if (accountDeleted) {
    return (
      <div
        className="fixed inset-0 bg-white flex flex-col items-center justify-center z-50 text-center px-8"
        role="status"
      >
        <h1 className="text-2xl font-bold text-stone-900 mb-4">Your account has been deleted.</h1>
        <p className="text-base text-stone-600">All your data has been permanently removed.</p>
        <p className="text-sm text-stone-400 mt-4">Redirecting you home…</p>
      </div>
    );
  }

  if (loading) {
    return (
      <main className="min-h-dvh bg-stone-50 flex flex-col items-center py-12 px-4">
        <div className="w-full max-w-2xl space-y-6">
          <div className="text-stone-500 text-sm">Loading...</div>
        </div>
      </main>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <main className="min-h-dvh bg-stone-50 flex flex-col items-center py-12 px-4">
      <div className="w-full max-w-2xl space-y-6">
        {/* Header card */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-serif text-stone-900">My Profile</h1>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-amber-600 hover:underline font-medium"
            >
              ← Dashboard
            </button>
          </div>
          <p className="text-stone-600 text-sm">
            {isFirstFill
              ? 'Tell us about yourself so your memoir feels personal from the very first question.'
              : 'Update your biographical details — Claude uses these to ask more personal questions.'}
          </p>
          {identity?.email && (
            <p className="text-stone-500 text-sm mt-3">
              Signed in as <span className="font-medium text-stone-800">{identity.email}</span>
            </p>
          )}
        </div>

        {/* Form card */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
          {/* Error banner */}
          {saveError && (
            <div className="mb-4 text-red-700 bg-red-50 border border-red-200 rounded p-3 text-sm">
              {saveError}
            </div>
          )}

          {isFirstFill ? (
            /* ── WIZARD MODE (3-step) ────────────────────────────────────── */
            <div className="space-y-6">
              {/* Progress indicator */}
              <div>
                <p className="text-sm text-stone-400">
                  Step {currentStep + 1} of 3
                </p>
              </div>

              {/* Section heading */}
              <div>
                <h2 className="text-xl font-semibold text-stone-900">
                  {WIZARD_3_HEADINGS[WIZARD_3_STEPS[currentStep]].heading}
                </h2>
                {WIZARD_3_HEADINGS[WIZARD_3_STEPS[currentStep]].subheading && (
                  <p className="text-sm text-stone-500 mt-1">
                    {WIZARD_3_HEADINGS[WIZARD_3_STEPS[currentStep]].subheading}
                  </p>
                )}
                {savedSection && (
                  <span className="text-sm text-green-600">Saved ✓</span>
                )}
              </div>

              {/* Section content — step 0: name + birthday; step 1: hometown only; step 2: family */}
              {currentStep === 0 && (
                <div className="space-y-5">
                  <div>
                    <label className={LABEL_CLS} htmlFor="wizard-fullName">Your name</label>
                    <input
                      id="wizard-fullName"
                      type="text"
                      className={INPUT_CLS}
                      value={profile.fullName ?? ''}
                      onChange={(e) => updateField('fullName', e.target.value)}
                      onBlur={() => handleBlurSave('basic')}
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLS} htmlFor="wizard-birthday">Birth year</label>
                    <input
                      id="wizard-birthday"
                      type="text"
                      className={INPUT_CLS}
                      placeholder="e.g. 1965"
                      value={profile.birthday ?? ''}
                      onChange={(e) => updateField('birthday', e.target.value)}
                      onBlur={() => handleBlurSave('basic')}
                    />
                  </div>
                </div>
              )}
              {currentStep === 1 && (
                <div className="space-y-5">
                  <div>
                    <label className={LABEL_CLS} htmlFor="wizard-hometown">City, State or Country</label>
                    <input
                      id="wizard-hometown"
                      type="text"
                      className={INPUT_CLS}
                      placeholder="e.g. Detroit, Michigan"
                      value={profile.hometown ?? ''}
                      onChange={(e) => updateField('hometown', e.target.value)}
                      onBlur={() => handleBlurSave('basic')}
                    />
                  </div>
                </div>
              )}
              {currentStep === 2 && renderSection('family')}

              {/* Navigation */}
              <div className="pt-4 border-t border-stone-100 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    {currentStep > 0 && (
                      <button type="button" onClick={handleBack} className={SECONDARY_BTN}>
                        ← Back
                      </button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={handleSkip} className={SECONDARY_BTN}>
                      Skip for now
                    </button>
                    {currentStep < WIZARD_3_STEPS.length - 1 ? (
                      <button
                        type="button"
                        onClick={handleNext}
                        className={PRIMARY_BTN}
                        disabled={currentStep === 0 && !canAdvance()}
                      >
                        Continue →
                      </button>
                    ) : (
                      <button type="button" onClick={handleFinish} className={PRIMARY_BTN}>
                        Finish →
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="flex flex-wrap justify-center gap-5">
                    <button type="button" onClick={handleStartOver} className="text-sm text-stone-400 hover:text-stone-600 transition-colors">
                      Start over
                    </button>
                    <button type="button" onClick={() => navigate('/')} className="text-sm text-stone-400 hover:text-stone-600 transition-colors">
                      Skip to Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* ── TABBED MODE ─────────────────────────────────────────────── */
            <div className="space-y-6">
              {/* Tab bar */}
              <div className="border-b border-stone-200 flex gap-6 flex-wrap">
                {TAB_LABELS.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={
                      activeTab === tab
                        ? 'px-0 py-3 border-b-2 border-amber-500 text-stone-900 font-semibold text-sm'
                        : 'px-0 py-3 border-b-2 border-transparent text-stone-500 hover:text-stone-700 text-sm'
                    }
                  >
                    {tab === 'settings' ? 'Settings' : STEP_LABELS[tab as WizardStep]}
                  </button>
                ))}
              </div>

              {/* Section heading with saved indicator */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-stone-900">
                  {activeTab === 'settings' ? 'Settings' : STEP_LABELS[activeTab as WizardStep]}
                </h2>
                {savedSection === activeTab && (
                  <span className="text-sm text-green-600">Saved ✓</span>
                )}
              </div>

              {/* Section content */}
              {renderSection(currentSection)}

              {/* Footer */}
              <div className="flex justify-end pt-4 border-t border-stone-100">
                <div className="flex flex-wrap items-center justify-end gap-3">
                  <p className="text-xs text-stone-500 mr-auto">
                    Email accounts can reset their password from the sign-in screen. Apple and Google passwords stay with those providers.
                  </p>
                  <button type="button" onClick={() => void signOut()} className={SECONDARY_BTN}>Sign out</button>
                  <button type="button" onClick={() => navigate('/')} className={PRIMARY_BTN}>Done</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
